import bboxPolygon from '@turf/bbox-polygon';
import intersect from '@turf/intersect';
import { isPointInsidePolygon } from '@deckee/utilities';
import COUNTRY_AND_STATE_BOUNDS from '../../configurations/geofencing-area-constants';

/**
 * Check if the area defined by bounds is outside of the viewable area
 *
 * @param {string} bounds - defined in the geofencing polygons
 * @param {object} viewableMapBounds - {bottom: float, left: float, right: float, top: float}
 * @param {float} viewableMapBounds.bottom - The bottom side of the viewable area
 * @param {float} viewableMapBounds.left - The left side of the viewable area
 * @param {float} viewableMapBounds.right - The right side of the viewable area
 * @param {float} viewableMapBounds.top - The top side of the viewable area
 * @returns bool `true` if the defined bounds are outside viewable area
 */
export const boundsOutsideViewableArea = (bounds, viewableMapBounds) => {
  const boundingArea = COUNTRY_AND_STATE_BOUNDS[bounds].geometry.coordinates[0];

  // NOTE: the points are in [longitude, latitude] format
  const latitudes = boundingArea.map((lngLat) => lngLat[1]);
  const latitudesMin = Math.min(...latitudes);
  const latitudesMax = Math.max(...latitudes);

  const longitudes = boundingArea.map((lngLat) => lngLat[0]);
  const longitudesMin = Math.min(...longitudes);
  const longitudesMax = Math.max(...longitudes);

  // This accounts for longitude values looping around from -180 to 180
  let adjustedRightBound = viewableMapBounds.right;
  if (viewableMapBounds.left > adjustedRightBound) adjustedRightBound += 360;

  const outsideBounds =
    viewableMapBounds.left > longitudesMax ||
    adjustedRightBound < longitudesMin ||
    viewableMapBounds.top < latitudesMin ||
    viewableMapBounds.bottom > latitudesMax;

  return outsideBounds;
};

export const boundsOverlapsViewableArea = (bounds, map) => {
  const mapboxBounds = map.getBounds();
  // Mapbox bounds are in the format: [[west, south], [east, north]]
  const mapboxWest = mapboxBounds.getWest();
  const mapboxSouth = mapboxBounds.getSouth();
  const mapboxEast = mapboxBounds.getEast();
  const mapboxNorth = mapboxBounds.getNorth();

  // Convert to Turf.js bounding box format: [minX, minY, maxX, maxY]
  const bbox = [mapboxWest, mapboxSouth, mapboxEast, mapboxNorth];

  // Convert the bounding box to a GeoJSON Polygon
  const x = bboxPolygon(bbox);

  const boundingArea = COUNTRY_AND_STATE_BOUNDS[bounds];

  try {
    return !!intersect(boundingArea, x);
  } catch (e) {
    // If there are any errors we just want to exclude the individual item rather than throwing an error
    return false;
  }
};

export default function getBoundingAreasByLocation({ longitude, latitude }) {
  const currentLocationSafetyInformation = [];
  // Checks for the current position being within certain bounds
  for (const [key, value] of Object.entries(COUNTRY_AND_STATE_BOUNDS)) {
    if (isPointInsidePolygon([longitude, latitude], value)) {
      currentLocationSafetyInformation.push(key);
    }
  }

  return currentLocationSafetyInformation;
}
